import './App.css';
import './Survey.css';
import React, { useEffect, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { sendLinkToApp } from './nativeBridge';
import { API_URL, PRIVACY_POLICY_URL } from './config';

// Layout encoded as rows: favorite is 2-3, improvements is 2-1-2 (matches the Figma design)
const FAVORITE_ROWS = [
  ['Sound Clarity', 'Noise Reduction'],
  ['Comfort', 'Easy to Use', 'Other'],
];
const IMPROVEMENT_ROWS = [
  ['Better Battery Life', 'Easier To Use'],
  ['Improved Sound Quality'],
  ['More Comfortable Fit', 'Other'],
];

const CONSENT_YES = 1;
const CONSENT_NO = 0;

const getUrlContext = () => {
  const params = new URLSearchParams(window.location.search);
  const rawProductType = params.get('productType');
  const parsedProductType = rawProductType === null ? null : parseInt(rawProductType, 10);

  return {
    authorization: params.get('authorization') || '',
    productType: Number.isNaN(parsedProductType) ? null : parsedProductType,
    email: params.get('email') || '',
    phone: params.get('phone') || '',
  };
};

const StarRating = ({ value, onChange }) => (
  <div className="survey-star-rating">
    {[1, 2, 3, 4, 5].map((star) => {
      const isSelected = star <= value;
      return (
        <span key={star} className="survey-star-item" onClick={() => onChange(star)}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill={isSelected ? '#F05627' : 'none'}
            stroke="#F05627"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </span>
      );
    })}
  </div>
);

function Survey({ onRequestComplete }) {
  const urlContext = getUrlContext();

  const [isDisabled, setIsDisabled] = useState(false);
  const contactRef = useRef(null);

  const [hearingAidRating, setHearingAidRating] = useState(0);
  const [recommendRating, setRecommendRating] = useState(0);
  const [favoriteSet, setFavoriteSet] = useState(new Set());
  const [improvementSet, setImprovementSet] = useState(new Set());

  const [followUpConsent, setFollowUpConsent] = useState(null);
  const [followUpContact, setFollowUpContact] = useState('');
  const [profile, setProfile] = useState({ email: '', phone: '' });

  useEffect(() => {
    if (!urlContext.authorization) return;
    const headers = { Authorization: urlContext.authorization };
    const profilePromise = fetch(`${API_URL}/user/getUserById`, { headers })
      .then(r => r.ok ? r.json() : null).catch(() => null);
    const lastQuery = urlContext.productType ? `?productType=${urlContext.productType}` : '';
    const lastPromise = fetch(`${API_URL}/info/last${lastQuery}`, { headers })
      .then(r => r.ok ? r.json() : null).catch(() => null);
    Promise.all([profilePromise, lastPromise]).then(([prof, last]) => {
      if (prof && prof.code === 200 && prof.data) {
        setProfile({ email: prof.data.email || '', phone: prof.data.phone || '' });
      }
      if (last && last.code === 200 && last.data) {
        const d = last.data;
        if (d.rating) setHearingAidRating(parseInt(d.rating, 10) || 0);
        if (d.nps) setRecommendRating(parseInt(d.nps, 10) || 0);
        if (d.favoriteFt) {
          setFavoriteSet(new Set(d.favoriteFt.split(',').map(s => s.trim()).filter(Boolean)));
        }
        if (d.improvements) {
          setImprovementSet(new Set(d.improvements.split(',').map(s => s.trim()).filter(Boolean)));
        }
        if (d.followUpConsent === CONSENT_YES || d.followUpConsent === CONSENT_NO) {
          setFollowUpConsent(d.followUpConsent);
        }
        if (d.followUpContact) setFollowUpContact(d.followUpContact);
      }
    });
  }, [API_URL, urlContext.authorization, urlContext.productType]);

  const toggleFromSet = (setter, current, value) => {
    const next = new Set(current);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };

  const handleSelectYes = () => {
    setFollowUpConsent(CONSENT_YES);
    if (!followUpContact) {
      const fallback = profile.email || profile.phone || urlContext.email || urlContext.phone || '';
      if (fallback) setFollowUpContact(fallback);
    }
  };

  const handleSelectNo = () => setFollowUpConsent(CONSENT_NO);

  const sendFeedback = (callback) => {
    const httpRequest = new XMLHttpRequest();
    httpRequest.open('POST', `${API_URL}/info/add/v2`, true);
    httpRequest.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    if (urlContext.authorization) {
      httpRequest.setRequestHeader('Authorization', urlContext.authorization);
    }
    const data = JSON.stringify({
      productType: urlContext.productType ?? 0,
      rating: hearingAidRating ? String(hearingAidRating) : '',
      favoriteFt: Array.from(favoriteSet).join(','),
      improvements: Array.from(improvementSet).join(','),
      nps: recommendRating ? String(recommendRating) : '',
      followUpConsent: followUpConsent === CONSENT_YES ? 1 : 0,
      followUpContact: followUpConsent === CONSENT_YES ? followUpContact.trim() : null,
    });
    httpRequest.send(data);
    httpRequest.onreadystatechange = function () {
      if (httpRequest.readyState === 4 && httpRequest.status === 200) {
        callback(httpRequest.responseText);
      }
    };
  };

  const callbackSubmit = (json) => {
    try {
      const obj = window.JSON.parse(json);
      if (obj.code === 200) {
        onRequestComplete();
      } else {
        toast.error('Feedback submission failed: ' + obj.message, { position: 'top-center' });
        setIsDisabled(false);
      }
    } catch (error) {
      toast.error('Error submitting feedback.', { position: 'top-center' });
      setIsDisabled(false);
    }
  };

  // Submit 启用条件：rating、两个 chip 问题各至少一选、followUp 选了。contact 始终可选。
  const isFormValid =
    hearingAidRating > 0
    && favoriteSet.size > 0
    && improvementSet.size > 0
    && followUpConsent !== null;

  const handleSubmit = () => {
    if (!isFormValid) {
      return;
    }
    setIsDisabled(true);
    sendFeedback(callbackSubmit);
  };

  return (
    <div className="survey-page">
      <div className="survey-scroll-content">
        <div className="survey-section-white">
          <div className="survey-section-inner">
            <div className="survey-intro">
              <span className="survey-intro-bold">OVER 1.5 BILLION</span>{' '}
              people worldwide experience hearing loss, yet 83% go without help. At ELEHEAR, we believe quality hearing support should be accessible to everyone. Your survey feedback helps us make this vision a reality. Thank you for being part of this meaningful change.
            </div>

            <div className="survey-field-group">
              <label className="survey-label">How would you rate your hearing aids?</label>
              <StarRating value={hearingAidRating} onChange={setHearingAidRating} />
            </div>

            <div className="survey-field-group">
              <label className="survey-label">What's your favorite feature of the product?</label>
              <div className="pc-chip-grid">
                {FAVORITE_ROWS.map((row, idx) => (
                  <div className="pc-chip-row" key={idx}>
                    {row.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={`pc-chip ${favoriteSet.has(opt) ? 'pc-chip--active' : ''}`}
                        onClick={() => toggleFromSet(setFavoriteSet, favoriteSet, opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="survey-field-group">
              <label className="survey-label">What improvements do you think the product still needs?</label>
              <div className="pc-chip-grid">
                {IMPROVEMENT_ROWS.map((row, idx) => (
                  <div className="pc-chip-row" key={idx}>
                    {row.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        className={`pc-chip ${improvementSet.has(opt) ? 'pc-chip--active' : ''}`}
                        onClick={() => toggleFromSet(setImprovementSet, improvementSet, opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="survey-field-group">
              <label className="survey-label">How likely are you to recommend our product to friends and family?</label>
              <StarRating value={recommendRating} onChange={setRecommendRating} />
            </div>

            <div className="survey-field-group">
              <label className="survey-label">Would you like us to follow up with you?</label>

              <div
                className={`pc-radio-row ${followUpConsent === CONSENT_YES ? 'pc-radio-row--active' : ''}`}
                onClick={handleSelectYes}
              >
                <span className={`pc-radio-dot ${followUpConsent === CONSENT_YES ? 'pc-radio-dot--checked' : ''}`} />
                <span className="pc-radio-text">Yes, I'm happy to be contacted</span>
              </div>

              {followUpConsent === CONSENT_YES && (
                <div className="pc-contact-block">
                  <div className="pc-contact-hint">Please leave your phone number or email address:</div>
                  <input
                    ref={contactRef}
                    className="pc-contact-input"
                    placeholder=""
                    value={followUpContact}
                    onChange={(e) => setFollowUpContact(e.target.value)}
                  />
                </div>
              )}

              <div
                className={`pc-radio-row ${followUpConsent === CONSENT_NO ? 'pc-radio-row--active' : ''}`}
                onClick={handleSelectNo}
              >
                <span className={`pc-radio-dot ${followUpConsent === CONSENT_NO ? 'pc-radio-dot--checked' : ''}`} />
                <span className="pc-radio-text">No, I prefer not to be contacted</span>
              </div>
            </div>

            <div className="survey-submit-section">
              <p className="survey-privacy">
                By submitting, you agree to our{' '}
                <a
                  href={PRIVACY_POLICY_URL}
                  rel="noopener noreferrer"
                  className="survey-privacy-link"
                  onClick={(e) => {
                    e.preventDefault();
                    sendLinkToApp(PRIVACY_POLICY_URL);
                  }}
                >
                  Privacy Policy
                </a>
                . We will strictly protect your personal information.
              </p>
              <button
                disabled={isDisabled}
                className={`survey-submit-btn ${isFormValid && !isDisabled ? 'active' : ''}`}
                onClick={handleSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      <Toaster />
    </div>
  );
}

export default Survey;
